import { cn } from "@/lib/utils";

interface LoadingAnimationProps {
  size?: "sm" | "md" | "lg";
  className?: string;
}

const LoadingAnimation = ({ size = "md", className }: LoadingAnimationProps) => {
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-16 h-16",
    lg: "w-24 h-24",
  };

  return (
    <div className={cn("flex items-center justify-center", className)}>
      <div className="relative">
        {/* Outer rotating ring */}
        <div
          className={cn(
            sizeClasses[size],
            "rounded-full border-4 border-transparent",
            "border-t-holographic-cyan border-r-holographic-pink",
            "animate-spin"
          )}
        />
        
        {/* Inner pulsing circle */}
        <div
          className={cn(
            "absolute inset-0 m-auto",
            size === "sm" ? "w-3 h-3" : size === "md" ? "w-6 h-6" : "w-9 h-9",
            "rounded-full",
            "bg-gradient-to-br from-holographic-cyan to-holographic-pink",
            "animate-[holographic-pulse_1.5s_ease-in-out_infinite]"
          )}
        />
        
        {/* Glow effect */}
        <div
          className={cn(
            sizeClasses[size],
            "absolute inset-0 rounded-full",
            "bg-gradient-to-br from-holographic-cyan/30 to-holographic-pink/30",
            "blur-lg animate-pulse"
          )}
        />
      </div>
    </div>
  );
};

const LoadingDots = ({ className }: { className?: string }) => {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="w-3 h-3 rounded-full bg-gradient-to-br from-holographic-cyan to-holographic-pink"
          style={{
            animation: `holographic-pulse 1.4s ease-in-out infinite`,
            animationDelay: `${i * 0.2}s`,
          }}
        />
      ))}
    </div>
  );
};

const LoadingBar = ({ className }: { className?: string }) => {
  return (
    <div className={cn("w-full h-1 bg-muted rounded-full overflow-hidden", className)}>
      <div
        className="h-full bg-gradient-to-r from-holographic-cyan via-holographic-pink to-holographic-cyan"
        style={{
          animation: "skeleton-shimmer 2s ease-in-out infinite",
          backgroundSize: "200% 100%",
        }}
      />
    </div>
  );
};

export { LoadingAnimation, LoadingDots, LoadingBar };
